import { SignUp, useClerk, useSignIn, useSignUp } from '@clerk/react-router';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { LoadingSpinner } from '~/components';
import {
  CLERK_REDIRECT_URL_PARAM,
  OAUTH_COMPLETION_URL_PARAM,
  sanitizeOAuthCompletionUrl,
} from '~/routing/returnTo';
import { paths } from '~/routing/routes';
import {
  getSessionTaskPath,
  readSessionTaskCompletionTarget,
  rememberSessionTaskCompletionTarget,
} from '~/routing/sessionTaskHandoff';
import { getClerkErrorMessage } from './clerkError';

const SIGN_UP_CONTINUE_PATH = `${paths.publicAuth.ssoCallback}/sign-up`;

const withQueryParam = (path: string, key: string, value: string) => {
  const params = new URLSearchParams({ [key]: value });
  return `${path}?${params.toString()}`;
};

export const SSOCallbackPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const clerk = useClerk();
  const { signIn, fetchStatus: signInFetchStatus } = useSignIn();
  const { signUp, fetchStatus: signUpFetchStatus } = useSignUp();
  const hasRun = useRef(false);
  const isMounted = useRef(false);
  const [callbackError, setCallbackError] = useState<string | null>(null);
  const rawCompletionUrl = new URLSearchParams(location.search).get(OAUTH_COMPLETION_URL_PARAM);
  const completionUrl =
    sanitizeOAuthCompletionUrl(rawCompletionUrl, window.location.origin) ??
    readSessionTaskCompletionTarget() ??
    paths.publicAuth.authComplete;
  const isSignUpContinuation =
    location.pathname === SIGN_UP_CONTINUE_PATH ||
    location.pathname.startsWith(`${SIGN_UP_CONTINUE_PATH}/`);

  // The continuation component owns its eventual finalize call, so remember the
  // destination as soon as this route mounts as well as immediately before the
  // explicit finalize/setActive calls below.
  useEffect(() => {
    rememberSessionTaskCompletionTarget(completionUrl);
  }, [completionUrl]);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (
      isSignUpContinuation ||
      !clerk.loaded ||
      signInFetchStatus === 'fetching' ||
      signUpFetchStatus === 'fetching' ||
      hasRun.current
    ) {
      return;
    }

    hasRun.current = true;

    const showError = (error: unknown, fallback = 'Authentication could not be completed.') => {
      // Core 3 changes fetchStatus while finalize/create is in flight. That
      // legitimately reruns this effect so it can observe fetching -> idle,
      // but it does not mean the page unmounted or the in-flight callback was
      // abandoned. Only suppress the update after a real component unmount.
      if (isMounted.current) setCallbackError(getClerkErrorMessage(error, fallback));
    };

    const navigateToSignIn = () => {
      navigate(withQueryParam(paths.publicAuth.signIn, CLERK_REDIRECT_URL_PARAM, completionUrl), {
        replace: true,
      });
    };

    const navigateToSignUp = () => {
      navigate(withQueryParam(SIGN_UP_CONTINUE_PATH, OAUTH_COMPLETION_URL_PARAM, completionUrl), {
        replace: true,
      });
    };

    const navigateToApp = ({
      session,
      decorateUrl,
    }: {
      session?: { currentTask?: { key?: string } | null } | null;
      decorateUrl: (url: string) => string;
    }) => {
      rememberSessionTaskCompletionTarget(completionUrl);

      // ClerkProvider.taskUrls normally takes precedence over this callback.
      // Keep a programmatic fallback so a provider/configuration regression
      // cannot leave a pending user on this loading page.
      const taskKey = session?.currentTask?.key;
      const target = taskKey ? getSessionTaskPath(taskKey) : completionUrl;
      if (!target) {
        showError(null, 'Clerk returned an unsupported session task. Please start again.');
        return;
      }

      const destination = new URL(decorateUrl(target), window.location.origin);
      if (destination.origin !== window.location.origin) {
        window.location.assign(destination.toString());
        return;
      }

      navigate(`${destination.pathname}${destination.search}${destination.hash}`, {
        replace: true,
      });
    };

    const finalizeSignIn = async () => {
      rememberSessionTaskCompletionTarget(completionUrl);
      const { error } = await signIn.finalize({ navigate: navigateToApp });
      if (error) {
        showError(error);
        return false;
      }
      return true;
    };

    const finalizeSignUp = async () => {
      rememberSessionTaskCompletionTarget(completionUrl);
      const { error } = await signUp.finalize({ navigate: navigateToApp });
      if (error) {
        showError(error);
        return false;
      }
      return true;
    };

    const activateExistingSession = async (sessionId: string) => {
      rememberSessionTaskCompletionTarget(completionUrl);
      await clerk.setActive({ session: sessionId, navigate: navigateToApp });
    };

    void (async () => {
      const signInStatus = () => signIn.status as string | null;
      const signUpStatus = () => signUp.status as string | null;

      const continueSignIn = async () => {
        if (signInStatus() === 'complete') {
          await finalizeSignIn();
          return;
        }

        if (signIn.existingSession?.sessionId) {
          await activateExistingSession(signIn.existingSession.sessionId);
          return;
        }

        if (signInStatus() === 'needs_first_factor') {
          if (
            signIn.supportedFirstFactors?.length &&
            signIn.supportedFirstFactors.every((factor) => factor.strategy === 'enterprise_sso')
          ) {
            showError(
              null,
              'Enterprise sign-in could not be completed. Please return to sign in and try again.'
            );
            return;
          }
          navigateToSignIn();
          return;
        }

        if (
          [
            'needs_identifier',
            'needs_second_factor',
            'needs_client_trust',
            'needs_new_password',
            'needs_protect_check',
          ].includes(signInStatus() ?? '')
        ) {
          // The prebuilt SignIn route resumes the current attempt and supports
          // MFA, Device Trust, password reset, and Protect challenge UI.
          navigateToSignIn();
          return;
        }

        showError(null, 'Sign-in returned an unsupported state. Please start again.');
      };

      const continueSignUp = async () => {
        if (signUpStatus() === 'complete') {
          await finalizeSignUp();
          return;
        }

        if (signUp.existingSession?.sessionId) {
          await activateExistingSession(signUp.existingSession.sessionId);
          return;
        }

        if (signUpStatus() === 'missing_requirements') {
          navigateToSignUp();
          return;
        }

        if (signUpStatus() === 'abandoned') {
          showError(null, 'This authentication attempt expired. Please start again.');
          return;
        }

        showError(null, 'Sign-up returned an unsupported state. Please start again.');
      };

      if (signInStatus() === 'complete') {
        await finalizeSignIn();
        return;
      }

      // An attempted sign-up can discover an existing account. Transfer its
      // verified external identity into SignIn and continue there.
      if (signUp.isTransferable) {
        const { error } = await signIn.create({ transfer: true });
        if (error) {
          showError(error);
          return;
        }
        await continueSignIn();
        return;
      }

      // The normal sign-up path starts with SignIn.sso(). A new external
      // account is transferable to SignUp; the prebuilt continuation handles
      // arbitrary missing fields, verification, CAPTCHA, and Protect checks.
      if (signIn.isTransferable) {
        const { error } = await signUp.create({ transfer: true });
        if (error) {
          showError(error);
          return;
        }
        await continueSignUp();
        return;
      }

      if (signUpStatus() === 'complete') {
        await finalizeSignUp();
        return;
      }

      const existingSessionId =
        signIn.existingSession?.sessionId ?? signUp.existingSession?.sessionId;
      if (existingSessionId) {
        await activateExistingSession(existingSessionId);
        return;
      }

      // Also support a direct SignUp.sso attempt left by an older tab or an
      // interrupted deployment. Clerk's stock callback handler omits this case.
      if (signUpStatus() === 'missing_requirements') {
        await continueSignUp();
        return;
      }

      if (
        [
          'needs_identifier',
          'needs_first_factor',
          'needs_second_factor',
          'needs_client_trust',
          'needs_new_password',
          'needs_protect_check',
        ].includes(signInStatus() ?? '')
      ) {
        await continueSignIn();
        return;
      }

      if (signUpStatus() === 'abandoned') {
        await continueSignUp();
        return;
      }

      showError(null, 'Authentication returned an unsupported state. Please start again.');
    })().catch((error: unknown) => {
      showError(error);
    });
  }, [
    clerk,
    clerk.loaded,
    completionUrl,
    isSignUpContinuation,
    navigate,
    signIn,
    signInFetchStatus,
    signUp,
    signUpFetchStatus,
  ]);

  if (isSignUpContinuation) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-primary-t-950 px-4">
        <SignUp
          routing="path"
          path={SIGN_UP_CONTINUE_PATH}
          signInUrl={withQueryParam(
            paths.publicAuth.signIn,
            CLERK_REDIRECT_URL_PARAM,
            completionUrl
          )}
          forceRedirectUrl={completionUrl}
        />
      </main>
    );
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-primary-t-950 px-4">
      {callbackError ? (
        <div role="alert" className="flex max-w-md flex-col items-center gap-3 text-center">
          <p className="text-sm text-danger">{callbackError}</p>
          <button
            type="button"
            className="text-sm text-primary-50 hover:underline"
            onClick={() =>
              navigate(
                withQueryParam(paths.publicAuth.signIn, CLERK_REDIRECT_URL_PARAM, completionUrl),
                { replace: true }
              )
            }
          >
            Return to sign in
          </button>
        </div>
      ) : (
        <LoadingSpinner size={24} gap={4} />
      )}
      <div id="clerk-captcha" />
    </div>
  );
};
